import { initialsOf, type PersonPhoto } from '@ft/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './http';

/**
 * Profilbilder: einmal je Sitzung die Liste der Personen mit Bild laden.
 * Avatare finden ihre Person über ID, E-Mail oder Namen (Verlauf liefert nur den Namen).
 */
interface Ctx { byKey: Map<string, PersonPhoto>; reload: () => void }
const PeopleCtx = createContext<Ctx>({ byKey: new Map(), reload: () => {} });

export function PeopleProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<PersonPhoto[]>([]);
  const reload = useCallback(() => { api<PersonPhoto[]>('GET', '/api/people/photos').then(setList).catch(() => setList([])); }, []);
  useEffect(() => { reload(); }, [reload]);
  const byKey = useMemo(() => {
    const m = new Map<string, PersonPhoto>();
    for (const p of list) { m.set(p.id, p); m.set(p.email.toLowerCase(), p); m.set(p.name.toLowerCase(), p); }
    return m;
  }, [list]);
  return <PeopleCtx.Provider value={{ byKey, reload }}>{children}</PeopleCtx.Provider>;
}

export const usePeople = () => useContext(PeopleCtx);

/** Profilbild der Person (falls vorhanden) */
export function usePhoto(p: { id?: string | null; email?: string | null; name?: string | null }): PersonPhoto | null {
  const { byKey } = usePeople();
  return (p.id && byKey.get(p.id)) || (p.email && byKey.get(p.email.toLowerCase())) || (p.name && byKey.get(p.name.toLowerCase())) || null;
}

export const photoUrl = (p: PersonPhoto) => `/api/users/${p.id}/photo?v=${p.v}`;

/**
 * Rundes Bild der Person; ohne Bild die Initialen (wie bisher).
 * kind: „chip“ = kleine Initialen-Plakette in Listen, „avatar“ = Kopfzeile.
 */
export function Avatar({ id, email, name, initials, size = 26, kind = 'chip', title }: {
  id?: string | null; email?: string | null; name: string; initials?: string; size?: number; kind?: 'chip' | 'avatar' | 'dot'; title?: string;
}) {
  const photo = usePhoto({ id, email, name });
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size };
  if (photo && !broken) return <img className="avimg" src={photoUrl(photo)} alt="" title={title ?? name} style={style} loading="lazy" onError={() => setBroken(true)} />;
  if (kind === 'dot') return null;
  const ini = initials ?? initialsOf(name);
  return <span className={kind === 'avatar' ? 'avatar' : 'pmc'} title={title ?? name} style={kind === 'chip' ? { ...style, fontSize: Math.max(9, Math.round(size * 0.4)) } : style}>{ini}</span>;
}

/** Name mit kleinem Bild davor */
export function Who({ id, name, initials, size = 22 }: { id?: string | null; name: string; initials?: string; size?: number }) {
  return <span className="who"><Avatar id={id} name={name} initials={initials} size={size} />{name}</span>;
}

/** Punkt im Änderungsverlauf: Bild der Person, sonst der bisherige Kreis */
export function HistMark({ user, imp }: { user: string | null; imp?: boolean }) {
  const photo = usePhoto({ name: user });
  if (photo) return <img className="avimg lmimg" src={photoUrl(photo)} alt="" title={user ?? undefined} style={{ width: 24, height: 24 }} loading="lazy" />;
  return <i className={`lm${imp ? ' imp' : ''}`} />;
}
